import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { addItem } from "../services/cartService";
import wishlistService from '../services/wishlistService';
import { getProductById, rateProduct } from "../services/productService";
import { getTradeInCampaign, evaluateItemCampaign } from "../services/campaignService";
import { getCategories } from "../services/categoryService";
import { useCurrency } from '../context/CurrencyContext';
import { useStickers } from '../context/StickerContext';
import { useLoanCampaign } from '../context/LoanContext';
import ProductSticker from '../components/ProductSticker';
import ProductLoanBadge from '../components/ProductLoanBadge';
import ProductLoanWidget from '../components/ProductLoanWidget';
import toast from 'react-hot-toast';
import API_BASE_URL from "../config";

export default function ProductView() {
  const { id } = useParams();
  const { formatPrice } = useCurrency();
  const { getStickersForProduct, categoryTree: stickerCatTree } = useStickers();
  const { getLoanForProduct, categoryTree: loanCatTree } = useLoanCampaign();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [mainIndex, setMainIndex] = useState(0);
  const [ratingLoading, setRatingLoading] = useState(false);
  const [inWishlist, setInWishlist] = useState(false);
  // product-level services are available on the fetched product payload; no separate state needed
  const [specsOpen, setSpecsOpen] = useState(true);
  const [qty, setQty] = useState(1);
  const [selectedServices, setSelectedServices] = useState([]);
  const [zoom, setZoom] = useState(false);
  const [zoomStyle, setZoomStyle] = useState({});
  const [campaign, setCampaign] = useState(null);
  const [categoryTree, setCategoryTree] = useState([]);

  useEffect(() => {
    let mounted = true;

    getTradeInCampaign().then(c => {
      if (mounted) setCampaign(c);
    }).catch(() => { });

    getCategories().then(cats => {
      if (mounted && Array.isArray(cats) && cats.length) {
        setCategoryTree(cats);
      }
    }).catch(() => { });

    const handleCampaignUpdate = (e) => {
      if (e?.detail?.campaign) {
        setCampaign(e.detail.campaign);
      } else {
        getTradeInCampaign(true).then(c => {
          if (mounted) setCampaign(c);
        }).catch(() => { });
      }
    };

    window.addEventListener('tradeInCampaignUpdated', handleCampaignUpdate);

    return () => {
      mounted = false;
      window.removeEventListener('tradeInCampaignUpdated', handleCampaignUpdate);
    };
  }, []);

  const effectiveCatTree = React.useMemo(() => {
    if (Array.isArray(categoryTree) && categoryTree.length) return categoryTree;
    if (Array.isArray(loanCatTree) && loanCatTree.length) return loanCatTree;
    if (Array.isArray(stickerCatTree) && stickerCatTree.length) return stickerCatTree;
    return [];
  }, [categoryTree, loanCatTree, stickerCatTree]);

  const tradeInInfo = React.useMemo(() => {
    if (!product || !campaign || !campaign.enabled) return null;
    return evaluateItemCampaign({
      item: product,
      campaign,
      product,
      categoryTree: effectiveCatTree
    });
  }, [product, campaign, effectiveCatTree]);

  const loanInfo = React.useMemo(() => {
    if (!product || !getLoanForProduct) return null;
    return getLoanForProduct(product);
  }, [product, getLoanForProduct]);

  const toggleService = (svc) => {
    setSelectedServices(prev => {
      if (prev.find(s => s.id === svc.id)) {
        return prev.filter(s => s.id !== svc.id);
      }
      return [...prev, { id: svc.id, name: svc.name, price: Number(svc.price || 0) }];
    });
  };

  // Out of stock inquiry modal state
  const [showRestockModal, setShowRestockModal] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleMouseMove = (e) => {
    const { left, top, width, height } = e.currentTarget.getBoundingClientRect();
    const x = ((e.pageX - left - window.scrollX) / width) * 100;
    const y = ((e.pageY - top - window.scrollY) / height) * 100;
    setZoomStyle({ transformOrigin: `${x}% ${y}%` });
    setZoom(true);
  };

  const handleMouseLeave = () => {
    setZoom(false);
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const p = await getProductById(id);
        if (!mounted) return;
        if (p?.error) return setError(p.error);
        setProduct(p);
        // Use only services attached to this product (admin-selected), server includes them on product fetch
        // services are stored on `product.services` and used directly where needed
      } catch (err) {
        if (mounted) setError('Failed to load product');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [id]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const list = await wishlistService.getWishlist();
        if (!mounted) return;
        setInWishlist(Array.isArray(list) && list.some(i => String(i.id) === String(id)));
      } catch (_) { if (mounted) setInWishlist(false); }
    })();
    const onUpdate = (e) => {
      const items = e && e.detail && e.detail.items ? e.detail.items : [];
      setInWishlist(Array.isArray(items) && items.some(i => String(i.id) === String(id)));
    };
    window.addEventListener('wishlistUpdated', onUpdate);
    return () => { mounted = false; window.removeEventListener('wishlistUpdated', onUpdate); };
  }, [id]);

  async function submitRating(idx) {
    if (ratingLoading || !product) return;
    // optimistic UI: show immediate feedback while request is in flight
    const prevAvg = product.ratingAvg || 0;
    const prevCount = product.ratingCount || 0;
    const optimisticAvg = Number(((prevAvg * prevCount) + idx) / (prevCount + 1)).toFixed(2);
    try {
      setRatingLoading(true);
      setProduct(prev => ({ ...prev, ratingAvg: Number(optimisticAvg), ratingCount: prevCount + 1 }));
      console.debug('Submitting rating', { productId: product.id, value: idx });
      const resp = await rateProduct(product.id, idx);
      if (resp && resp.average !== undefined) {
        setProduct(prev => ({ ...prev, ratingAvg: resp.average, ratingCount: resp.count }));
      } else if (resp && resp.error) {
        // revert optimistic update
        setProduct(prev => ({ ...prev, ratingAvg: prevAvg, ratingCount: prevCount }));
        toast.error(resp.error || 'Dështoi dërgimi i vlerësimit');
      } else {
        // unknown response shape: revert
        setProduct(prev => ({ ...prev, ratingAvg: prevAvg, ratingCount: prevCount }));
      }
    } catch (e) {
      console.error('Rating error', e);
      // revert optimistic update
      setProduct(prev => ({ ...prev, ratingAvg: prevAvg, ratingCount: prevCount }));
      toast.error('Dështoi dërgimi i vlerësimit');
    } finally {
      setRatingLoading(false);
    }
  }

  if (loading) return <div style={{ padding: 20 }}>Duke u ngarkuar...</div>;
  if (error) return <div style={{ padding: 20, color: 'red' }}>{error}</div>;
  if (!product) return null;

  const images = (product.images && product.images.length) ? product.images : (product.image ? [product.image] : []);
  const imageUrl = images[mainIndex] || images[0] || null;
  const specs = product.specs || product.specifications || {};
  const isOutOfStock = Number(product.stock || 0) <= 0;

  const displayModel = product.modeli || product.model || specs.Model || specs.Modeli || specs.model || '';
  const displayWarranty = product.garancia || product.warranty || specs.Warranty || specs.Garancia || specs.garancia || specs.warranty || '';

  const handleAddToCart = async () => {
    try {
      if (Number(product.stock || 0) <= 0) {
        toast.error('Produkti nuk është në stok');
        return;
      }
      const price = Number(product.offerPrice || product.salePrice || product.price || 0);
      const item = { id: product.id, name: product.name, image: imageUrl || '', price, services: selectedServices };
      await addItem(item, Math.max(1, Number(qty) || 1));
      try { navigate('/cart'); } catch (_) { }
    } catch (err) {
      console.error(err);
      toast.error('Dështoi shtimi në shportë');
    }
  };

  const toggleWishlist = async () => {
    try {
      await wishlistService.toggleItem(product);
      const list = await wishlistService.getWishlist();
      setInWishlist(Array.isArray(list) && list.some(i => String(i.id) === String(product.id)));
    } catch (err) {
      console.error(err);
      toast.error('Dështoi përditësimi i listës së dëshirave');
    }
  };

  const submitRestockInquiry = async (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      toast.error('Ju lutem plotësoni fushat e detyrueshme (Emri, Email, Mesazhi)');
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = {
        title: 'Kërkesë për rimbushje produkti (Out of Stock)',
        pageTitle: product.name,
        pageUrl: window.location.href,
        fields: [
          { label: 'Emri / Name', value: contactForm.name },
          { label: 'Email', value: contactForm.email },
          { label: 'Telefoni / Phone', value: contactForm.phone },
          { label: 'Mesazhi / Message', value: contactForm.message },
          { label: 'SKU e Produktit', value: product.sku || 'N/A' },
          { label: 'ID e Produktit', value: product.id }
        ]
      };

      const res = await fetch(`${API_BASE_URL.replace('/api', '')}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Dështoi dërgimi i mesazhit');

      toast.success('Kërkesa juaj u dërgua me sukses! Do t\'ju kontaktojmë së shpejti.');
      setShowRestockModal(false);
      setContactForm({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      console.error(err);
      toast.error('Gabim gjatë dërgimit. Ju lutem provoni përsëri më vonë.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main style={{ padding: '18px 0' }}>
      <div style={{ maxWidth: 1440, margin: '0 auto', padding: '0 18px' }}>
        <div className="product-view-grid">
          <div className="product-main-column">
            <section className="product-header-area" style={{ padding: 6 }}>
              <h2 style={{ marginTop: 12 }}>{product.name}</h2>
              <div style={{ color: '#666', marginBottom: 8 }}>{product.brand || product.category}</div>

              <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
                <div role="radiogroup" aria-label={`Rate ${product.name}`} style={{ display: 'flex', gap: 6 }}>
                  {Array.from({ length: 5 }).map((_, i) => {
                    const idx = i + 1;
                    const avg = Number(product.ratingAvg || 0);
                    const fillPct = Math.max(0, Math.min(100, Math.round((Math.max(0, avg - i) * 100))));
                    const gradId = `starGrad-${product.id || 'p'}-${i}`;
                    return (
                      <button
                        key={idx}
                        role="radio"
                        aria-checked={avg >= idx}
                        aria-label={`Rate ${idx} out of 5`}
                        onKeyDown={async (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); await submitRating(idx); } }}
                        onClick={async () => await submitRating(idx)}
                        disabled={ratingLoading}
                        style={{ background: 'transparent', border: 'none', padding: 4, cursor: 'pointer' }}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden focusable="false">
                          <defs>
                            <linearGradient id={gradId} x1="0%" x2="100%">
                              <stop offset={`${fillPct}%`} stopColor="#ffb400" />
                              <stop offset={`${fillPct}%`} stopColor="#ddd" />
                            </linearGradient>
                          </defs>
                          <path fill={`url(#${gradId})`} d="M12 .587l3.668 7.431 8.2 1.192-5.934 5.788 1.402 8.168L12 18.896l-7.336 3.87 1.402-8.168L.132 9.21l8.2-1.192z" />
                        </svg>
                      </button>
                    );
                  })}
                </div>

                <div style={{ color: '#666', fontSize: 14 }} aria-live="polite">
                  {product.ratingAvg ? `${product.ratingAvg} / 5` : 'Nuk ka vlerësim ende'}{product.ratingCount ? ` (${product.ratingCount} vota)` : ''}
                </div>
              </div>
            </section>

            <div
              className={`zoom-container ${zoom ? 'zoomed' : ''}`}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
              style={{ marginTop: 12, boxShadow: '0 6px 18px rgba(0,0,0,0.06)', position: 'relative' }}
            >
              {/* Custom Campaign Stickers */}
              {((product && getStickersForProduct) ? getStickersForProduct(product) : []).map(stk => (
                <ProductSticker key={stk.id} sticker={stk} />
              ))}

              {/* Custom Loan Campaign Badge */}
              {loanInfo && (
                <ProductLoanBadge loanInfo={loanInfo} placement="productView" />
              )}

              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={product.name}
                  className="product-main-image"
                  style={zoomStyle}
                  fetchPriority="high"
                  decoding="async"
                />
              ) : (
                <div className="product-main-image" />
              )}
            </div>

            {images && images.length > 0 && (
              <div className="product-thumbs-area" style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
                {images.map((src, i) => (
                  <button key={i} onClick={() => setMainIndex(i)} style={{ border: mainIndex === i ? '2px solid #0b74de' : '1px solid #eee', padding: 0, background: '#fafafa', borderRadius: 8, overflow: 'hidden' }}>
                    <img src={src} alt={`thumb-${i}`} className="product-thumb-small" loading="lazy" decoding="async" />
                  </button>
                ))}
              </div>
            )}

            <div className="product-details-area" style={{ marginTop: 18 }}>
              <div style={{
                display: 'flex',
                gap: 16,
                flexWrap: 'wrap',
                alignItems: 'center',
                background: '#f8fafc',
                padding: '12px 16px',
                borderRadius: 8,
                border: '1px solid #e2e8f0'
              }}>
                {product.sku && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ color: '#64748b', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.5px' }}>SKU:</span>
                    <strong style={{ color: '#0f172a', fontSize: 14 }}>{product.sku}</strong>
                  </div>
                )}

                {displayModel && (
                  <>
                    {product.sku && <div style={{ width: 1, height: 16, background: '#cbd5e1' }}></div>}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ color: '#64748b', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Modeli:</span>
                      <strong style={{ color: '#0f172a', fontSize: 14 }}>{displayModel}</strong>
                    </div>
                  </>
                )}

                {displayWarranty && (
                  <>
                    {(product.sku || displayModel) && <div style={{ width: 1, height: 16, background: '#cbd5e1' }}></div>}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ color: '#64748b', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Garancia:</span>
                      <strong style={{ color: '#0f172a', fontSize: 14 }}>{displayWarranty}</strong>
                    </div>
                  </>
                )}
              </div>
              <p style={{ marginTop: 12, lineHeight: 1.6 }}>{product.description}</p>

              {specs && typeof specs === 'object' && Object.keys(specs).length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f7f7f7', padding: '10px 12px', borderRadius: 6, cursor: 'pointer' }} onClick={() => setSpecsOpen(s => !s)}>
                    <div style={{ fontWeight: 700 }}>Specifikat e Produktit</div>
                    <div style={{ fontSize: 14, color: '#666' }}>{specsOpen ? '▾' : '▸'}</div>
                  </div>

                  {specsOpen && (
                    <div style={{ marginTop: 8, border: '1px solid #eee', borderRadius: 6, overflow: 'hidden' }}>
                      <div style={{ background: '#fafafa', padding: '8px 12px', borderBottom: '1px solid #eee', fontWeight: 700 }}>Specifikimet</div>
                      <div>
                        {Object.entries(specs).map(([k, v], idx) => (
                          <div key={k} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', borderBottom: idx !== Object.keys(specs).length - 1 ? '1px solid #f0f0f0' : 'none', background: '#fff' }}>
                            <div style={{ color: '#333' }}>{k}</div>
                            <div style={{ color: '#333', textAlign: 'right', minWidth: 120 }}>{String(v)}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <aside style={{ position: 'relative' }}>
            <div className="product-view-sticky" style={{ position: 'sticky', top: 20 }}>
              <div className="buybox">
                {/* Price block */}
                <div>
                  {product.offerPrice ? (
                    <div className="price-old">{formatPrice(product.price)}</div>
                  ) : null}
                  <div className="price-large">
                    {formatPrice(product.offerPrice || product.salePrice || product.price || 0)}
                  </div>
                </div>

                {/* Loan / Installment Campaign Widget */}
                {loanInfo && (
                  <ProductLoanWidget loanInfo={loanInfo} />
                )}

                {/* Trade-in Campaign Buybox Notice */}
                {Boolean(campaign?.enabled && tradeInInfo && tradeInInfo.isEligible && !tradeInInfo.isExcluded) && (
                  <div style={{
                    marginTop: 14,
                    padding: '12px 14px',
                    background: '#f0fdf4',
                    border: '1.5px solid #86efac',
                    borderRadius: 10,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12
                  }}>
                    <span style={{ fontSize: 22 }}>♻️</span>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#166534' }}>
                        {campaign?.name || 'Sill të vjetrën, merr të renë!'}
                      </div>
                      <div style={{ fontSize: 12, color: '#15803d', marginTop: 2 }}>
                        Përfitoni ulje bazë <strong>{tradeInInfo.baseDiscount}%</strong> automatikisht në shportë, ose deri në <strong>{tradeInInfo.fullDiscount}%</strong> zbritje duke dorëzuar pajisjen e vjetër.
                      </div>
                    </div>
                  </div>
                )}
                {Boolean(campaign?.enabled && campaign?.showExcludedNote !== false && tradeInInfo && tradeInInfo.isExcluded) && (
                  <div style={{
                    marginTop: 10,
                    fontSize: 12.5,
                    color: '#64748b',
                    fontWeight: 500
                  }}>
                    {campaign?.excludedNoteText || 'Nuk përfshihet në fushatën “Sill të vjetrën merr të renë”'}
                  </div>
                )}

                {product.services && product.services.length > 0 && (
                  <div style={{ marginTop: 16 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8, color: '#334155' }}>Shërbime shtesë opsionale:</div>
                    <div style={{ display: 'grid', gap: 6 }}>
                      {product.services.map(svc => {
                        const isFree = !svc.price || Number(svc.price) === 0;
                        const isSelected = !isFree && selectedServices.some(s => s.id === svc.id);
                        return (
                          <div
                            key={svc.id}
                            onClick={() => !isFree && toggleService(svc)}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              fontSize: 13,
                              background: 'transparent',
                              padding: '8px 0',
                              cursor: isFree ? 'default' : 'pointer',
                              borderBottom: '1px solid #f1f5f9',
                              transition: 'all 0.2s'
                            }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              {!isFree && (
                                <div style={{
                                  width: 16,
                                  height: 16,
                                  borderRadius: '50%',
                                  border: isSelected ? '4px solid #0ea5e9' : '1px solid #cbd5e1',
                                  background: '#fff',
                                  flexShrink: 0
                                }} />
                              )}
                              <div style={{ color: '#475569' }}>
                                <div style={{ fontWeight: 500, color: (isSelected || isFree) ? '#1e293b' : '#334155' }}>{svc.name}</div>
                                {svc.description && <div style={{ fontSize: 12, marginTop: 2, color: '#64748b' }}>{svc.description}</div>}
                              </div>
                            </div>
                            <span style={{ fontWeight: 600, color: (isSelected || isFree) ? '#0f172a' : '#334155' }}>
                              {isFree ? '' : `+${formatPrice(svc.price)}`}
                            </span>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
                {/* CTA, qty and wishlist */}
                <div style={{ marginTop: 16 }}>
                  <button
                    type="button"
                    onClick={handleAddToCart}
                    className="buy-cta"
                    disabled={isOutOfStock}
                    style={isOutOfStock ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
                  >
                    {isOutOfStock ? 'Jashtë stokut' : 'BLI TANI'}
                  </button>

                  {isOutOfStock && (
                    <button
                      type="button"
                      onClick={() => setShowRestockModal(true)}
                      className="buy-cta"
                      style={{
                        marginTop: 10,
                        background: '#f8fafc',
                        color: '#0f172a',
                        border: '1px solid #cbd5e1',
                        fontSize: 14,
                        padding: '10px 0'
                      }}
                    >
                      Pyet për këtë produkt
                    </button>
                  )}

                  <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center' }}>
                    <div className="qty-control">
                      <button type="button" onClick={() => setQty(q => Math.max(1, q - 1))} disabled={isOutOfStock}>-</button>
                      <div style={{ padding: '8px 12px', minWidth: 44, textAlign: 'center' }}>{qty}</div>
                      <button type="button" onClick={() => setQty(q => q + 1)} disabled={isOutOfStock}>+</button>
                    </div>
                    <button
                      type="button"
                      onClick={toggleWishlist}
                      className={`action-icon-btn wishlist-btn ${inWishlist ? 'active' : ''}`}
                      title={inWishlist ? "Hiq nga dëshirat" : "Shto në dëshira"}
                    >
                      <svg viewBox="0 0 24 24" fill={inWishlist ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon-svg">
                        <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={handleAddToCart}
                      title="Shto në shportë"
                      className="action-icon-btn cart-btn"
                      disabled={isOutOfStock}
                      style={isOutOfStock ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon-svg">
                        <circle cx="8" cy="21" r="1" />
                        <circle cx="19" cy="21" r="1" />
                        <path d="M2.05 2.05h1.5l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12" />
                      </svg>
                    </button>
                  </div>
                </div>

                <div style={{ marginTop: 12, color: '#666', fontSize: 13 }}>
                  {product.ratingAvg ? `${product.ratingAvg} / 5 (${product.ratingCount || 0} vota)` : 'Nuk ka vlerësim ende'}
                </div>
              </div>
            </div>
          </aside>
        </div>

        {/* Services modal removed from product view — services are managed in Cart */}
      </div>

      {showRestockModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
          background: 'rgba(9, 72, 89, 0.4)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
          backdropFilter: 'blur(6px)', padding: 16
        }}>
          <div style={{
            background: '#fff', borderRadius: 20, width: '100%', maxWidth: 520, padding: '32px 28px', boxShadow: '0 20px 40px rgba(0, 125, 165, 0.15)',
            maxHeight: '90vh', overflowY: 'auto', position: 'relative', border: '1.5px solid #e8edf2'
          }}>
            <button
              type="button"
              onClick={() => setShowRestockModal(false)}
              style={{ position: 'absolute', top: 18, right: 18, background: '#f0f8fb', border: '1.5px solid #d0e7f2', borderRadius: '50%', width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#007da5', transition: 'all 0.2s ease' }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
            <h3 style={{ margin: '0 0 10px 0', fontSize: '1.4rem', fontWeight: 700, color: '#094859' }}>Pyet për këtë produkt</h3>
            <p style={{ margin: '0 0 20px 0', color: '#64748b', fontSize: 14, lineHeight: 1.5 }}>
              Produkti <strong style={{ color: '#094859' }}>{product.name}</strong> aktualisht nuk është në stok. Plotësoni formën dhe ne do t'ju informojmë kur të jetë sërish i disponueshëm.
            </p>

            <form onSubmit={submitRestockInquiry}>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 13.5, fontWeight: 600, color: '#334155' }}>Emri dhe Mbiemri <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  type="text"
                  autoFocus
                  required
                  style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: '1.5px solid #e2e8f0', background: '#f8fafc', fontSize: 14.5, boxSizing: 'border-box' }}
                  value={contactForm.name}
                  onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                  placeholder="Emri juaj"
                />
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 13.5, fontWeight: 600, color: '#334155' }}>Email <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  type="email"
                  required
                  style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: '1.5px solid #e2e8f0', background: '#f8fafc', fontSize: 14.5, boxSizing: 'border-box' }}
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  placeholder="shembull@globe.al"
                />
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 13.5, fontWeight: 600, color: '#334155' }}>Numri i telefonit (Opsionale)</label>
                <input
                  type="tel"
                  style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: '1.5px solid #e2e8f0', background: '#f8fafc', fontSize: 14.5, boxSizing: 'border-box' }}
                  value={contactForm.phone}
                  onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                  placeholder="+355 6x xx xx xxx"
                />
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 13.5, fontWeight: 600, color: '#334155' }}>Mesazhi <span style={{ color: '#ef4444' }}>*</span></label>
                <textarea
                  required
                  rows={3}
                  style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: '1.5px solid #e2e8f0', background: '#f8fafc', fontSize: 14.5, fontFamily: 'inherit', boxSizing: 'border-box', resize: 'vertical' }}
                  value={contactForm.message}
                  onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                  placeholder="Dua të di nëse ky produkt do të vijë së shpejti..."
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                style={{
                  width: '100%', padding: '13px', background: '#007da5', color: '#fff', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: isSubmitting ? 'not-allowed' : 'pointer', transition: '0.2s', opacity: isSubmitting ? 0.7 : 1, boxShadow: '0 4px 14px rgba(0, 125, 165, 0.25)'
                }}
              >
                {isSubmitting ? 'Duke dërguar...' : 'Dërgo Kërkesën'}
              </button>
            </form>
          </div>
        </div>
      )}
    </main>
  );
}


